import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Shuffle } from 'lucide-react';
import { AGES, BUILDS, HAIR_COLORS, HAIR_STYLES, OUTFITS, SKIN_TONE_COUNT, normalizeLook, randomLook, type Look } from '../../shared/appearance';
import { drawFigure, HAIR_COLOR_VALUES, SKIN_COLORS } from '../game/guestFigure';
import { SvgPen } from '../game/svgPen';

const hex = (color: number) => `#${color.toString(16).padStart(6, '0')}`;

function figure(look: Look, seated: boolean) {
  const pen = new SvgPen();
  drawFigure(pen, look, { action: seated ? 'sit' : 'idle', phase: 0, walking: false, seated, facing: seated ? 'back' : 'front', own: false, reducedMotion: true });
  return pen.markup();
}

type Props = { value: Look; onChange: (look: Look) => void; onDone: () => void };

/** "Dáng của bạn" (docs/STORY.md §5.5): choose a figure, never declare an identity. */
export function LookPicker({ value, onChange, onDone }: Props) {
  const { t } = useTranslation();
  const front = useMemo(() => figure(value, false), [value]);
  const seated = useMemo(() => figure(value, true), [value]);
  const set = (patch: Partial<Look>) => onChange(normalizeLook({ ...value, ...patch }));
  const group = <K extends keyof Look>(key: K, label: string, options: readonly Look[K][], name: (option: Look[K]) => string) => (
    <div className="look-row">
      <span id={`look-${key}`} className="look-label">{label}</span>
      <div className="look-options" role="group" aria-labelledby={`look-${key}`}>
        {options.map(option => <button key={String(option)} type="button" aria-pressed={value[key] === option} onClick={() => set({ [key]: option } as Partial<Look>)}>{name(option)}</button>)}
      </div>
    </div>
  );
  return <div className="look-picker">
    <p className="modal-description">{t('look.description')}</p>
    <div className="look-preview" aria-hidden="true">
      <figure><svg viewBox="-34 -80 68 88" dangerouslySetInnerHTML={{ __html: front }} /><figcaption>{t('look.front')}</figcaption></figure>
      <figure><svg viewBox="-34 -70 68 78" dangerouslySetInnerHTML={{ __html: seated }} /><figcaption>{t('look.seated')}</figcaption></figure>
    </div>
    {group('age', t('look.age'), AGES, option => t(`look.ages.${option}`))}
    {group('build', t('look.build'), BUILDS, option => t(`look.builds.${option}`))}
    <div className="look-row">
      <span id="look-skin" className="look-label">{t('look.skin')}</span>
      <div className="look-options" role="group" aria-labelledby="look-skin">
        {Array.from({ length: SKIN_TONE_COUNT }, (_, index) => <button key={index} type="button" className="look-tone" style={{ background: hex(SKIN_COLORS[index]) }} aria-pressed={value.skin === index} aria-label={t('look.skinTone', { n: index + 1 })} title={t('look.skinTone', { n: index + 1 })} onClick={() => set({ skin: index })} />)}
      </div>
    </div>
    {group('hair', t('look.hair'), HAIR_STYLES, option => t(`look.hairs.${option}`))}
    <div className="look-row">
      <span id="look-hairColor" className="look-label">{t('look.hairColor')}</span>
      <div className="look-options" role="group" aria-labelledby="look-hairColor">
        {HAIR_COLORS.map(option => <button key={option} type="button" aria-pressed={value.hairColor === option} onClick={() => set({ hairColor: option })}><span className="look-dot" style={{ background: hex(HAIR_COLOR_VALUES[option]) }} aria-hidden="true" />{t(`look.hairColors.${option}`)}</button>)}
      </div>
    </div>
    {group('outfit', t('look.outfit'), OUTFITS, option => t(`look.outfits.${option}`))}
    <div className="look-row">
      <span id="look-extras" className="look-label">{t('look.extras')}</span>
      <div className="look-options" role="group" aria-labelledby="look-extras">
        <button type="button" aria-pressed={value.glasses} onClick={() => set({ glasses: !value.glasses })}>{t('look.glasses')}</button>
        <button type="button" aria-pressed={value.scarf} onClick={() => set({ scarf: !value.scarf })}>{t('look.scarf')}</button>
        <button type="button" aria-pressed={value.cane} disabled={value.age !== 'elder'} title={value.age !== 'elder' ? t('look.caneNote') : undefined} onClick={() => set({ cane: !value.cane })}>{t('look.cane')}</button>
      </div>
    </div>
    <div className="modal-buttons">
      <button type="button" className="secondary-button" onClick={() => onChange(randomLook())}><Shuffle size={16} />{t('look.random')}</button>
      <button type="button" className="primary-button" onClick={onDone} data-testid="look-done"><Check size={17} />{t('look.done')}</button>
    </div>
    <p className="modal-note">{t('look.note')}</p>
  </div>;
}
